import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeftIcon, AlertTriangleIcon, CreditCardIcon, CalendarIcon, ClockIcon, FileTextIcon, UploadIcon, CheckCircleIcon, XCircleIcon, DownloadIcon, TrashIcon, InfoIcon, HelpCircleIcon, XIcon as CloseIcon, ChevronLeftIcon, ChevronRightIcon, PenIcon, ChevronUpIcon, ChevronDownIcon, EyeIcon, MoreVerticalIcon, PinIcon, LinkIcon, QrCodeIcon, FileIcon, SmartphoneIcon, TerminalIcon, CopyIcon, CheckIcon } from 'lucide-react';
import { Dispute, DisputeStatus, DisputeStage, DisputeReason, EvidenceDocument } from '../types/Dispute';
import { getEvidenceRecommendation } from '../utils/evidenceRecommendations';
import { generateDisputes } from '../utils/disputes';
interface RelatedOrder {
  id: string;
  orderId: string;
  amount: number;
  currency: string;
  createdTime: string;
  status: string;
  matchingCriteria: number;
  totalCriteria: number;
}
interface MatchingCriteriaDetail {
  category: string;
  subtitle: string;
  items: {
    label: string;
    matched: boolean;
  }[];
}
export function DisputeDetail() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const location = useLocation();
  const [showEvidenceForm, setShowEvidenceForm] = useState(false);
  const [showAlert, setShowAlert] = useState(true);
  const [activeTab, setActiveTab] = useState<'payment' | 'related'>('payment');
  const [selectedOrder, setSelectedOrder] = useState<RelatedOrder | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showMatchingCriteriaTooltip, setShowMatchingCriteriaTooltip] = useState(false);
  const [showReasonTooltip, setShowReasonTooltip] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [showChallengeFlow, setShowChallengeFlow] = useState(false);
  const [challengeStep, setChallengeStep] = useState<'reason' | 'evidence' | 'order' | 'customer' | 'other' | 'review'>('reason');
  const [selectedChallengeReason, setSelectedChallengeReason] = useState('purchasing_history');
  const [selectedProductType, setSelectedProductType] = useState('');
  const [noEvidenceChecked, setNoEvidenceChecked] = useState(false);
  const [noOrderEvidenceChecked, setNoOrderEvidenceChecked] = useState(false);
  const [noCustomerEvidenceChecked, setNoCustomerEvidenceChecked] = useState(false);
  const [noOtherEvidenceChecked, setNoOtherEvidenceChecked] = useState(false);
  const [showNoEvidenceWarning, setShowNoEvidenceWarning] = useState(false);
  const [reviewConfirmChecked, setReviewConfirmChecked] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    challengeDetails: true,
    transactionDetails: true,
    otherEvidence: true
  });
  const [showChallengeDetails, setShowChallengeDetails] = useState(false);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  // State to track if dispute has been accepted
  const [isAccepted, setIsAccepted] = useState(false);
  // State to track if dispute has been challenged (separate from derived isChallenged)
  const [hasBeenChallenged, setHasBeenChallenged] = useState(false);
  // State for adding notes
  const [showAddNote, setShowAddNote] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [merchantNotes, setMerchantNotes] = useState<Array<{
    id: string;
    timestamp: string;
    description: string;
    status: 'note';
    author: string;
    isPinned?: boolean;
  }>>([]);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  const [showNoteMenu, setShowNoteMenu] = useState<string | null>(null);
  const [copiedOrderId, setCopiedOrderId] = useState<string | null>(null);
  // Check if returning from challenge submission
  useEffect(() => {
    if (location.state?.challenged) {
      setHasBeenChallenged(true);
    }
  }, [location.state]);
  // Close note menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showNoteMenu) {
        const target = event.target as HTMLElement;
        if (!target.closest('.note-menu-container')) {
          setShowNoteMenu(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNoteMenu]);
  const rowsPerPage = 5;
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  const handleChallengeClick = () => {
    navigate(`/disputes/${id}/challenge`);
  };
  const handleViewChallengeDetails = () => {
    setShowChallengeDetails(true);
  };
  const handleChallengeConfirm = () => {
    setShowChallengeModal(false);
    setShowChallengeFlow(true);
    setChallengeStep('reason');
  };
  const handleChallengeReasonNext = () => {
    setChallengeStep('evidence');
  };
  const handleEvidenceNext = () => {
    setChallengeStep('order');
  };
  const handleOrderNext = () => {
    setChallengeStep('customer');
  };
  const handleCustomerNext = () => {
    setChallengeStep('other');
  };
  const handleOtherNext = () => {
    // Check if user has provided no evidence at all
    if (noEvidenceChecked && noOrderEvidenceChecked && noCustomerEvidenceChecked && noOtherEvidenceChecked) {
      setShowNoEvidenceWarning(true);
    } else {
      setChallengeStep('review');
    }
  };
  const handleReviewBack = () => {
    setChallengeStep('other');
  };
  const handleCloseChallengeFlow = () => {
    setShowChallengeFlow(false);
    setChallengeStep('reason');
    setSelectedChallengeReason('purchasing_history');
    setSelectedProductType('');
    setNoEvidenceChecked(false);
    setNoOrderEvidenceChecked(false);
    setNoCustomerEvidenceChecked(false);
    setNoOtherEvidenceChecked(false);
    setShowNoEvidenceWarning(false);
    setReviewConfirmChecked(false);
  };
  const handleAcceptClick = () => {
    setShowAcceptModal(true);
  };
  const handleAcceptConfirm = () => {
    setIsAccepted(true);
    setShowAcceptModal(false);
  };
  const handleAddNote = () => {
    if (noteText.trim()) {
      const newNote = {
        id: `note-${Date.now()}`,
        timestamp: new Date().toISOString(),
        description: noteText.trim(),
        status: 'note' as const,
        author: 'Eng Han Ng',
        isPinned: false
      };
      setMerchantNotes([newNote, ...merchantNotes]);
      setNoteText('');
      setShowAddNote(false);
    }
  };
  const handleEditNote = (noteId: string) => {
    const note = merchantNotes.find(n => n.id === noteId);
    if (note) {
      setEditingNoteId(noteId);
      setEditingNoteText(note.description);
      setShowNoteMenu(null);
    }
  };
  const handleSaveEdit = (noteId: string) => {
    if (editingNoteText.trim()) {
      setMerchantNotes(merchantNotes.map(note => note.id === noteId ? {
        ...note,
        description: editingNoteText.trim()
      } : note));
      setEditingNoteId(null);
      setEditingNoteText('');
    }
  };
  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditingNoteText('');
  };
  const handleDeleteNote = (noteId: string) => {
    setMerchantNotes(merchantNotes.filter(note => note.id !== noteId));
    setShowNoteMenu(null);
  };
  const handlePinNote = (noteId: string) => {
    setMerchantNotes(merchantNotes.map(note => note.id === noteId ? {
      ...note,
      isPinned: !note.isPinned
    } : note));
    setShowNoteMenu(null);
  };
  const handleCopyOrderId = async (orderId: string) => {
    try {
      await navigator.clipboard.writeText(orderId);
      setCopiedOrderId(orderId);
      setTimeout(() => setCopiedOrderId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  const getFileFormatRequirements = () => {
    const cardBrand = dispute.cardBrand.toLowerCase();
    // Check for Amex
    if (cardBrand === 'amex') {
      return {
        formats: 'Supported formats: TIFF, PDF, JPEG, JPG, PNG, DOC, Maximum individual file size: 4MB, Maximum PDF pages: less than 20 pages, Maximum image size: Not more than 300 x 300 DPI',
        language: 'Evidence language: English',
        files: 'Supported files: TIFF, TIF, PDF, JPEG, JPG, DOC, PNG and maximum combined file sizes: 25MB across all files.'
      };
    }
    // Check for Mastercard
    if (cardBrand === 'mastercard') {
      return {
        formats: 'Accepted file formats: TIFF, PDF, JPEG, PNG. File name restrictions: Use both English characters and numbers. Maximum document / image size: Not more than 300 x 300 DPI and pixel count should be less than 30 million',
        language: 'Evidence language: English',
        files: 'Supported files: TIFF, TIF, PDF, JPEG, JPG, PNG and maximum combined file sizes: 14.5MB across all files.'
      };
    }
    // Visa and other card brands (default)
    return {
      formats: 'Supported formats: TIFF, PDF, PNG, JPEG. Maximum individual file size: 2MB. Maximum page size for TIFF and JPEG: 8.64 inches x 14 inches',
      language: 'Evidence language: English',
      files: 'Supported files: TIFF, TIF, PDF, PNG, JPEG, JPG and maximum combined file sizes: 10MB across all files.'
    };
  };
  // Mock related orders data
  const relatedOrders: RelatedOrder[] = [{
    id: '1',
    orderId: '8b89fb9a-3fce-4aea-956f-49aa8d033caa',
    amount: 10000,
    currency: 'THB',
    createdTime: '2025-07-06 09:01:43 UTC+0800',
    status: 'Completed',
    matchingCriteria: 6,
    totalCriteria: 7
  }, {
    id: '2',
    orderId: 'bcd5898f-5af4-4bb8-99ee-6f2a308d799b',
    amount: 10000,
    currency: 'THB',
    createdTime: '2025-07-06 09:01:41 UTC+0800',
    status: 'Completed',
    matchingCriteria: 6,
    totalCriteria: 7
  }, {
    id: '3',
    orderId: '4086dc8d-fb0b-44d0-8e8c-cf018f0c778d',
    amount: 10000,
    currency: 'THB',
    createdTime: '2025-07-06 08:49:46 UTC+0800',
    status: 'Completed',
    matchingCriteria: 6,
    totalCriteria: 7
  }, {
    id: '4',
    orderId: 'a5cf0edf-9412-4ffe-9275-b8914ba25e25',
    amount: 10000,
    currency: 'THB',
    createdTime: '2025-07-06 08:49:33 UTC+0800',
    status: 'Completed',
    matchingCriteria: 6,
    totalCriteria: 7
  }, {
    id: '5',
    orderId: '6d92fede-b0c8-4efd-b633-230703873 56f',
    amount: 10000,
    currency: 'THB',
    createdTime: '2025-07-06 08:49:31 UTC+0800',
    status: 'Completed',
    matchingCriteria: 7,
    totalCriteria: 7
  }];
  const matchingCriteriaDetails: MatchingCriteriaDetail[] = [{
    category: 'Customer information',
    subtitle: 'Must match at least two criteria',
    items: [{
      label: 'IP address',
      matched: true
    }, {
      label: 'Device ID / fingerprint',
      matched: true
    }, {
      label: 'Shipping address',
      matched: true
    }, {
      label: 'Email address',
      matched: false
    }]
  }, {
    category: 'Transaction information',
    subtitle: 'Must match at least two criteria',
    items: [{
      label: '120 - 365 days older',
      matched: true
    }, {
      label: 'Undisputed & not fraudulent',
      matched: true
    }]
  }, {
    category: 'Additional information',
    subtitle: '',
    items: [{
      label: 'Payment method',
      matched: true
    }]
  }];
  const totalPages = Math.ceil(relatedOrders.length / rowsPerPage);
  const paginatedOrders = relatedOrders.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);
  // Find the dispute by ID using centralized data
  const allDisputes = useMemo(() => generateDisputes(), []);
  const dispute = useMemo(() => {
    const found = allDisputes.find(d => d.id === id);
    if (!found) {
      // Return first dispute as fallback
      return allDisputes[0];
    }
    return found;
  }, [id, allDisputes]);
  // Check if dispute is challenged - either from the dispute status or from navigation state
  const isChallenged = dispute.status === 'challenged' || location.state?.challenged === true || hasBeenChallenged;
  // Get effective status (use accepted if user just accepted it)
  const effectiveStatus = isAccepted ? 'accepted' : dispute.status;
  const paymentAmount = dispute.amount;
  const disputedAmount = dispute.amount;
  const disputeFee = Math.round(dispute.amount * 0.08);
  const netAmount = disputedAmount + disputeFee;
  const formatCurrency = (amount: number, currency: string = 'THB') => {
    const value = (amount / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `THB ${value}`;
  };
  const getStatusBadge = (status: DisputeStatus) => {
    const styles = {
      requires_response: 'text-[#92400E] bg-[#FFEDD5]',
      expired: 'text-[#374151] bg-[#E5E7EB]',
      challenged: 'text-[#1D4ED8] bg-[#DBEAFE]',
      accepted: 'text-[#7C2D12] bg-[#FFEDD5]',
      reversed: 'text-[#166534] bg-[#DCFCE7]',
      pending_closure: 'text-[#4B5563] bg-[#E5E7EB]',
      pending_decision: 'text-[#1D4ED8] bg-[#DBEAFE]',
      won: 'text-[#166534] bg-[#DCFCE7]',
      lost: 'text-[#B91C1C] bg-[#FEE2E2]'
    };
    const labels = {
      requires_response: 'Requires response',
      expired: 'Expired',
      challenged: 'Challenged',
      accepted: 'Accepted',
      reversed: 'Reversed',
      pending_closure: 'Pending Closure',
      pending_decision: 'Pending Decision',
      won: 'Won',
      lost: 'Lost'
    };
    return <span className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium ${styles[status]}`} data-id="element-7674">
        {labels[status]}
      </span>;
  };
  const getHoursRemaining = (dueDate: string) => {
    const now = new Date();
    const due = new Date(dueDate);
    const diffMs = due.getTime() - now.getTime();
    const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));
    return diffHours;
  };
  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const getReasonDescription = (reason: DisputeReason): string => {
    const descriptions: Record<DisputeReason, string> = {
      duplicate: 'The customer claims this is a duplicate charge',
      fraudulent: 'The customer claims the transaction was fraudulent',
      subscription_canceled: 'The customer claims they canceled their subscription',
      product_unacceptable: 'The customer claims the product was unacceptable',
      product_not_received: 'The customer claims they did not receive the product',
      unrecognized: 'The customer does not recognize the transaction',
      credit_not_processed: 'The customer claims a credit was not processed',
      incorrect_account_details: 'The customer claims incorrect account details were used',
      insufficient_funds: 'The customer claims they had insufficient funds',
      bank_cannot_process: 'The bank cannot process the transaction',
      debit_not_authorized: 'The customer claims the debit was not authorized',
      general: 'General dispute reason',
      customer_initiated: 'Customer initiated the dispute',
      check_returned: 'The check was returned',
      noncompliant: 'The transaction was noncompliant'
    };
    return descriptions[reason] || 'Dispute reason not specified';
  };
  const getReasonLabel = (reason: DisputeReason): string => {
    const labels: Record<DisputeReason, string> = {
      duplicate: 'Duplicate',
      fraudulent: 'Fraudulent',
      subscription_canceled: 'Subscription canceled',
      product_unacceptable: 'Product unacceptable',
      product_not_received: 'Product not received',
      unrecognized: 'Unrecognized',
      credit_not_processed: 'Credit not processed',
      incorrect_account_details: 'Incorrect account details',
      insufficient_funds: 'Insufficient funds',
      bank_cannot_process: 'Bank cannot process',
      debit_not_authorized: 'Debit not authorized',
      general: 'General',
      customer_initiated: 'Customer initiated',
      check_returned: 'Check returned',
      noncompliant: 'Noncompliant'
    };
    return labels[reason];
  };
  const getPaymentSource = () => {
    // In a real app, this would come from the dispute data
    // For now, we'll use a simple logic based on dispute ID
    const sources = ['Payment Link', 'QR Code', 'Invoice', 'Tap to Phone', 'POS Terminal'];
    const sourceIcons = [LinkIcon, QrCodeIcon, FileIcon, SmartphoneIcon, TerminalIcon];
    const index = parseInt(dispute.id) % sources.length;
    return {
      name: sources[index],
      Icon: sourceIcons[index]
    };
  };
  const hoursRemaining = getHoursRemaining(dispute.dueDate);
  const reversedLifecycle = useMemo(() => {
    const lifecycle = [...dispute.lifecycle];
    if (isChallenged) {
      lifecycle.push({
        id: '3',
        timestamp: new Date().toISOString(),
        status: 'challenged' as DisputeStatus,
        description: 'Dispute challenged with supporting evidence'
      });
    }
    if (isAccepted) {
      lifecycle.push({
        id: String(lifecycle.length + 1),
        timestamp: new Date().toISOString(),
        status: 'accepted' as DisputeStatus,
        description: 'Dispute accepted by merchant'
      });
    }
    // Separate pinned and unpinned notes
    const pinnedNotes = merchantNotes.filter(note => note.isPinned);
    const unpinnedNotes = merchantNotes.filter(note => !note.isPinned);
    // Sort unpinned notes by timestamp
    const sortedUnpinnedNotes = unpinnedNotes.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    // Combine: pinned notes first (sorted by timestamp), then lifecycle events and unpinned notes
    const allEvents = [...lifecycle, ...sortedUnpinnedNotes];
    const sortedEvents = allEvents.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    // Add pinned notes at the top
    return [...pinnedNotes.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()), ...sortedEvents];
  }, [dispute.lifecycle, isChallenged, isAccepted, merchantNotes]);
  // Get evidence recommendations using the centralized utility
  const evidenceRecs = useMemo(() => {
    // LOGGING: Start of evidence recommendation lookup
    console.group('🔍 Evidence Recommendation Lookup');
    console.log('Dispute ID:', dispute.id);
    console.log('Card Brand (raw):', dispute.cardBrand);
    console.log('Network Reason Code (raw):', dispute.networkReasonCode);
    console.log('Dispute Reason:', dispute.reason);
    const result = getEvidenceRecommendation(dispute.cardBrand, dispute.networkReasonCode);
    console.log('Lookup Result:', {
      title: result.title,
      description: result.description,
      recommendationsCount: result.recommendations.length,
      recommendations: result.recommendations.map(r => r.title)
    });
    console.groupEnd();
    return result;
  }, [dispute.cardBrand, dispute.networkReasonCode]);
  // If challenge flow is active, show full-screen challenge interface
  if (showChallengeFlow) {
    return <div className="min-h-screen bg-white" data-id="element-7675">
        {/* Header */}
        <div className="border-b border-gray-200" data-id="element-7676">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-7677">
            <div className="flex items-center gap-3" data-id="element-7678">
              <div className="flex items-center gap-2" data-id="element-7679">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-7680">
                  <path d="M3 7L12 2L21 7L12 12L3 7Z" fill="#FF6B35" stroke="#FF6B35" strokeWidth="2" strokeLinejoin="round" data-id="element-7681" />
                  <path d="M3 12L12 17L21 12" stroke="#FF6B35" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-7682" />
                  <path d="M3 17L12 22L21 17" stroke="#FF6B35" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-7683" />
                </svg>
                <span className="text-lg font-semibold" data-id="element-7684">Airwallex</span>
              </div>
              <span className="text-gray-400" data-id="element-7685">|</span>
              <span className="text-gray-700" data-id="element-7686">Challenge dispute</span>
            </div>
            <button onClick={handleCloseChallengeFlow} className="text-gray-400 hover:text-gray-600" data-id="element-7687">
              <CloseIcon size={24} data-id="element-7688" />
            </button>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-8" data-id="element-7689">
          <div className="flex gap-8" data-id="element-7690">
            {/* Left Sidebar */}
            <div className="w-64 flex-shrink-0" data-id="element-7691">
              <div className="space-y-1" data-id="element-7692">
                <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between ${expandedSections.challengeDetails ? 'bg-gray-100' : ''}`} onClick={() => toggleSection('challengeDetails')} data-id="element-7693">
                  <span className="font-medium" data-id="element-7694">Challenge details</span>
                  {expandedSections.challengeDetails ? <ChevronUpIcon size={16} data-id="element-7695" /> : <ChevronDownIcon size={16} data-id="element-7696" />}
                </button>
                {expandedSections.challengeDetails && <div className="ml-3 space-y-1" data-id="element-7697">
                    <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'reason' ? 'text-blue-600 bg-blue-50' : 'text-gray-700'}`} data-id="element-7698">
                      {['evidence', 'order', 'customer', 'other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-7699" />}
                      Challenge reason
                    </button>
                    <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'evidence' ? 'text-blue-600 bg-blue-50' : challengeStep === 'reason' ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-7700">
                      {['order', 'customer', 'other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-7701" />}
                      Supporting evidence
                    </button>
                  </div>}

                <button className="w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between text-gray-500" onClick={() => toggleSection('transactionDetails')} data-id="element-7702">
                  <span data-id="element-7703">Transaction details</span>
                  {expandedSections.transactionDetails ? <ChevronUpIcon size={16} data-id="element-7704" /> : <ChevronDownIcon size={16} data-id="element-7705" />}
                </button>
                {expandedSections.transactionDetails && <div className="ml-3 space-y-1" data-id="element-7706">
                    <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'order' ? 'text-blue-600 bg-blue-50' : ['reason', 'evidence'].includes(challengeStep) ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-7707">
                      {['customer', 'other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-7708" />}
                      Order details
                    </button>
                    <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'customer' ? 'text-blue-600 bg-blue-50' : ['reason', 'evidence', 'order'].includes(challengeStep) ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-7709">
                      {['other', 'review'].includes(challengeStep) && <CheckCircleIcon size={16} className="text-green-600" data-id="element-7710" />}
                      Customer details
                    </button>
                  </div>}

                <button className="w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between text-gray-500" onClick={() => toggleSection('otherEvidence')} data-id="element-7711">
                  <span data-id="element-7712">Other evidence</span>
                  {expandedSections.otherEvidence ? <ChevronUpIcon size={16} data-id="element-7713" /> : <ChevronDownIcon size={16} data-id="element-7714" />}
                </button>
                {expandedSections.otherEvidence && <div className="ml-3 space-y-1" data-id="element-7715">
                    <button className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${challengeStep === 'other' ? 'text-blue-600 bg-blue-50' : ['reason', 'evidence', 'order', 'customer'].includes(challengeStep) ? 'text-gray-500' : 'text-gray-700'}`} data-id="element-7716">
                      {challengeStep === 'review' && <CheckCircleIcon size={16} className="text-green-600" data-id="element-7717" />}
                      Other supporting documents
                    </button>
                  </div>}
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1" data-id="element-7718">
              {challengeStep === 'reason' && <div className="max-w-3xl" data-id="element-7719">
                  <div className="flex items-start justify-between mb-6" data-id="element-7720">
                    <div data-id="element-7721">
                      <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-7722">
                        Challenge reason
                      </h1>
                      <p className="text-gray-600" data-id="element-7723">
                        Your responses will help us recommend the best evidence
                        to submit for your challenge.
                      </p>
                    </div>
                    <div className="text-right" data-id="element-7724">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-7725">
                        Dispute ID: {dispute.disputeId}
                      </div>
                      <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-7726">
                        View details
                      </button>
                      <div className="mt-2 text-sm" data-id="element-7727">
                        <div className="text-gray-500" data-id="element-7728">Dispute amount</div>
                        <div className="font-medium" data-id="element-7729">
                          {formatCurrency(dispute.amount)}
                        </div>
                      </div>
                      <div className="mt-2 text-sm" data-id="element-7730">
                        <div className="text-gray-500" data-id="element-7731">Response due</div>
                        <div className="font-medium" data-id="element-7732">
                          {formatDateTime(dispute.dueDate)}
                        </div>
                        <div className="text-red-600 font-medium" data-id="element-7733">
                          Due in 1 hour
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6" data-id="element-7734">
                    <div data-id="element-7735">
                      <label className="block text-sm font-medium text-gray-900 mb-3" data-id="element-7736">
                        Why are you challenging the dispute?
                      </label>
                      <p className="text-sm text-gray-600 mb-3" data-id="element-7737">
                        Choose the closest option.
                      </p>
                      <div className="space-y-3" data-id="element-7738">
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-7739">
                          <input type="radio" name="challengeReason" value="withdrew" checked={selectedChallengeReason === 'withdrew'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-1" data-id="element-7740" />
                          <span className="text-sm text-gray-900" data-id="element-7741">
                            The customer withdrew the dispute
                          </span>
                        </label>
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-7742">
                          <input type="radio" name="challengeReason" value="refunded" checked={selectedChallengeReason === 'refunded'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-1" data-id="element-7743" />
                          <span className="text-sm text-gray-900" data-id="element-7744">
                            The customer has already been refunded
                          </span>
                        </label>
                        <label className="flex items-start gap-3 p-3 border-2 border-blue-600 bg-blue-50 rounded-lg cursor-pointer" data-id="element-7745">
                          <input type="radio" name="challengeReason" value="purchasing_history" checked={selectedChallengeReason === 'purchasing_history'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-1" data-id="element-7746" />
                          <div className="flex-1" data-id="element-7747">
                            <div className="text-sm text-gray-900 font-medium" data-id="element-7748">
                              The customer has a purchasing history with me
                            </div>
                            {selectedChallengeReason === 'purchasing_history' && <div className="mt-3 p-3 bg-white rounded border border-blue-200" data-id="element-7749">
                                <p className="text-sm text-gray-700" data-id="element-7750">
                                  We recommend this reason as we have identified
                                  transactions that prove the customer has a
                                  purchasing history with you.
                                </p>
                              </div>}
                          </div>
                        </label>
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-7751">
                          <input type="radio" name="challengeReason" value="other" checked={selectedChallengeReason === 'other'} onChange={e => setSelectedChallengeReason(e.target.value)} className="mt-1" data-id="element-7752" />
                          <span className="text-sm text-gray-900" data-id="element-7753">
                            Other reasons
                          </span>
                        </label>
                      </div>
                    </div>

                    <div data-id="element-7754">
                      <label className="block text-sm font-medium text-gray-900 mb-3" data-id="element-7755">
                        What type of product/service was the disputed payment
                        for?
                      </label>
                      <p className="text-sm text-gray-600 mb-3" data-id="element-7756">
                        Choose the closest option.
                      </p>
                      <div className="space-y-3" data-id="element-7757">
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-7758">
                          <input type="radio" name="productType" value="physical" checked={selectedProductType === 'physical'} onChange={e => setSelectedProductType(e.target.value)} className="mt-1" data-id="element-7759" />
                          <div data-id="element-7760">
                            <div className="text-sm text-gray-900 font-medium" data-id="element-7761">
                              Physical product
                            </div>
                            <div className="text-xs text-gray-500 mt-1" data-id="element-7762">
                              Physical goods to be delivered to the customer,
                              e.g. clothes, accessories, electronic gadgets
                            </div>
                          </div>
                        </label>
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-7763">
                          <input type="radio" name="productType" value="digital" checked={selectedProductType === 'digital'} onChange={e => setSelectedProductType(e.target.value)} className="mt-1" data-id="element-7764" />
                          <div data-id="element-7765">
                            <div className="text-sm text-gray-900 font-medium" data-id="element-7766">
                              Digital product or service
                            </div>
                            <div className="text-xs text-gray-500 mt-1" data-id="element-7767">
                              Purchased access to software, digital files or
                              experiences, e.g. mobile apps, music service,
                              digital tickets
                            </div>
                          </div>
                        </label>
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-7768">
                          <input type="radio" name="productType" value="offline" checked={selectedProductType === 'offline'} onChange={e => setSelectedProductType(e.target.value)} className="mt-1" data-id="element-7769" />
                          <div data-id="element-7770">
                            <div className="text-sm text-gray-900 font-medium" data-id="element-7771">
                              Offline service
                            </div>
                            <div className="text-xs text-gray-500 mt-1" data-id="element-7772">
                              Labour or assistant services provided either at a
                              specific location or remotely, e.g.
                              transportation, home servicing
                            </div>
                          </div>
                        </label>
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-7773">
                          <input type="radio" name="productType" value="reservation" checked={selectedProductType === 'reservation'} onChange={e => setSelectedProductType(e.target.value)} className="mt-1" data-id="element-7774" />
                          <div data-id="element-7775">
                            <div className="text-sm text-gray-900 font-medium" data-id="element-7776">
                              Reservation or booking
                            </div>
                            <div className="text-xs text-gray-500 mt-1" data-id="element-7777">
                              Services related to reservations or bookings, e.g.
                              flight tickets, hotel booking
                            </div>
                          </div>
                        </label>
                        <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50" data-id="element-7778">
                          <input type="radio" name="productType" value="other_product" checked={selectedProductType === 'other_product'} onChange={e => setSelectedProductType(e.target.value)} className="mt-1" data-id="element-7779" />
                          <span className="text-sm text-gray-900" data-id="element-7780">Other</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-7781">
                    <button onClick={handleCloseChallengeFlow} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-7782">
                      Cancel
                    </button>
                    <button onClick={handleChallengeReasonNext} disabled={!selectedProductType} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-7783">
                      Next
                    </button>
                  </div>
                </div>}

              {challengeStep === 'evidence' && <div className="max-w-3xl" data-id="element-7784">
                  <div className="flex items-start justify-between mb-6" data-id="element-7785">
                    <div data-id="element-7786">
                      <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-7787">
                        {evidenceRecs.title}
                      </h1>
                      <p className="text-gray-600" data-id="element-7788">
                        {evidenceRecs.description}
                        {evidenceRecs.learnMoreLink && <>
                            {' '}
                            <a href="#" className="text-blue-600 hover:text-blue-700" data-id="element-7789">
                              {evidenceRecs.learnMoreLink}
                            </a>
                          </>}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-7790">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-7791">
                        Dispute ID: {dispute.disputeId}
                      </div>
                      <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-7792">
                        View details
                      </button>
                      <div className="mt-2 text-sm" data-id="element-7793">
                        <div className="text-gray-500" data-id="element-7794">Dispute amount</div>
                        <div className="font-medium" data-id="element-7795">
                          {formatCurrency(dispute.amount)}
                        </div>
                      </div>
                      <div className="mt-2 text-sm" data-id="element-7796">
                        <div className="text-gray-500" data-id="element-7797">Response due</div>
                        <div className="font-medium" data-id="element-7798">
                          {formatDateTime(dispute.dueDate)}
                        </div>
                        <div className="text-red-600 font-medium" data-id="element-7799">
                          Due in 1 hour
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mb-6" data-id="element-7800">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-7801">
                      Provide supporting evidence
                    </h3>
                    {/* LOGGING: Verify evidence is being rendered */}
                    {console.log('🎨 Rendering evidence step with:', {
                  title: evidenceRecs.title,
                  recommendationsCount: evidenceRecs.recommendations.length,
                  firstRecommendation: evidenceRecs.recommendations[0]
                })}
                    {dispute.networkReasonCode === '13.2' && <p className="text-sm text-gray-600 mb-4" data-id="element-7802">
                        For Visa 13.2 (Subscription Canceled) disputes, we
                        recommend you provide the following evidence:
                      </p>}
                    {dispute.reason === 'subscription_canceled' && dispute.networkReasonCode !== '13.2' && <p className="text-sm text-gray-600 mb-4" data-id="element-7803">
                          For subscription cancellation disputes, we recommend
                          you provide the following evidence:
                        </p>}
                    {dispute.reason !== 'subscription_canceled' && <p className="text-sm text-gray-600 mb-4" data-id="element-7804">
                        Based on your challenge reason, we recommend providing:
                      </p>}
                    <ul className="list-disc pl-5 space-y-2 text-sm text-gray-700 mb-4" data-id="element-7805">
                      {evidenceRecs.recommendations.map((rec, index) => <li key={index} data-id="element-7806">
                          <strong data-id="element-7807">{rec.title}</strong>
                          {rec.description && `: ${rec.description}`}
                        </li>)}
                    </ul>
                    <p className="text-sm text-gray-500" data-id="element-7808">
                      {(() => {
                    const requirements = getFileFormatRequirements();
                    return <>
                            {requirements.formats}
                            <br data-id="element-7809" />
                            {requirements.language}
                            <br data-id="element-7810" />
                            {requirements.files}
                          </>;
                  })()}
                    </p>
                  </div>

                  <div className="mb-6" data-id="element-7811">
                    <button disabled={noEvidenceChecked} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-7812">
                      <UploadIcon size={16} data-id="element-7813" />
                      Upload file
                    </button>
                  </div>

                  <div className="mb-6" data-id="element-7814">
                    <textarea placeholder="Include any additional file descriptions or explanations here. (Optional)" rows={4} disabled={noEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-7815" />
                  </div>

                  <div className="mb-6" data-id="element-7816">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-7817">
                      <input type="checkbox" checked={noEvidenceChecked} onChange={e => setNoEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-7818" />
                      <span className="text-sm text-gray-900" data-id="element-7819">
                        I do not have any evidence to provide for this step
                      </span>
                    </label>
                  </div>

                  <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-7820">
                    <button onClick={() => setChallengeStep('reason')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-7821">
                      Back
                    </button>
                    <button onClick={handleEvidenceNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-7822">
                      Next
                    </button>
                  </div>
                </div>}

              {challengeStep === 'order' && <div className="max-w-3xl" data-id="element-7823">
                  <div className="flex items-start justify-between mb-6" data-id="element-7824">
                    <div data-id="element-7825">
                      <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-7826">
                        Order details
                      </h1>
                      <p className="text-gray-600" data-id="element-7827">
                        Please provide as much information as possible about the
                        goods/service the payment was disputed for. This will
                        allow the issuer to verify that the evidence you
                        provided corresponds to the transaction disputed by the
                        customer.
                      </p>
                      <p className="text-gray-600 mt-2" data-id="element-7828">
                        The details below have been pre-filled from transaction
                        data. Please review and edit the information if needed.
                      </p>
                    </div>
                    <div className="text-right" data-id="element-7829">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-7830">
                        Dispute ID: {dispute.disputeId}
                      </div>
                      <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-7831">
                        View details
                      </button>
                      <div className="mt-2 text-sm" data-id="element-7832">
                        <div className="text-gray-500" data-id="element-7833">Dispute amount</div>
                        <div className="font-medium" data-id="element-7834">
                          {formatCurrency(dispute.amount)}
                        </div>
                      </div>
                      <div className="mt-2 text-sm" data-id="element-7835">
                        <div className="text-gray-500" data-id="element-7836">Response due</div>
                        <div className="font-medium" data-id="element-7837">
                          {formatDateTime(dispute.dueDate)}
                        </div>
                        <div className="text-red-600 font-medium" data-id="element-7838">
                          Due in 1 hour
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6" data-id="element-7839">
                    <div data-id="element-7840">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-7841">
                        Product details
                        <button className="ml-2 text-blue-600 hover:text-blue-700" data-id="element-7842">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-7843">
                            <path d="M8 12V8M8 4H8.01M15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-7844" />
                          </svg>
                        </button>
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-7845">
                        <span data-id="element-7846">IPHONE7 * 5, 100.01 THB</span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-7847">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-7848">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-7849" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-7850">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-7851">
                        Shipping address
                        <button className="ml-2 text-blue-600 hover:text-blue-700" data-id="element-7852">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-7853">
                            <path d="M8 12V8M8 4H8.01M15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-7854" />
                          </svg>
                        </button>
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-7855">
                        <span data-id="element-7856">
                          Rabbit Gateway Office, 123 Silom Road, Bang Rak,
                          Bangkok 10500, Thailand
                        </span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-7857">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-7858">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-7859" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-7860">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-7861">
                        Shipping carrier
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-7862">
                        <span data-id="element-7863">sameday</span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-7864">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-7865">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-7866" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-7867">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-7868">
                        Provide additional supporting evidence
                      </h3>
                      <p className="text-sm text-gray-600 mb-4" data-id="element-7869">
                        As the payment was for a{' '}
                        <span className="font-medium" data-id="element-7870">Physical product</span>,
                        we recommend you to also provide the following
                        information:
                      </p>
                      <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700 mb-4" data-id="element-7871">
                        <li data-id="element-7872">Shipping date</li>
                        <li data-id="element-7873">Shipping tracking number</li>
                      </ul>
                      <p className="text-sm text-gray-600 mb-4" data-id="element-7874">
                        You can upload any document containing such information
                        (e.g. invoices, shipping documentation, delivery
                        confirmation or shipping receipts) below.
                      </p>
                      <p className="text-sm text-gray-500 mb-4" data-id="element-7875">
                        {(() => {
                      const requirements = getFileFormatRequirements();
                      return <>
                              {requirements.formats}
                              <br data-id="element-7876" />
                              {requirements.language}
                              <br data-id="element-7877" />
                              {requirements.files}
                            </>;
                    })()}
                      </p>
                    </div>

                    <div data-id="element-7878">
                      <button disabled={noOrderEvidenceChecked} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noOrderEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-7879">
                        <UploadIcon size={16} data-id="element-7880" />
                        Upload file
                      </button>
                    </div>

                    <div data-id="element-7881">
                      <textarea placeholder="Include any additional file descriptions or explanations here. (Optional)" rows={4} disabled={noOrderEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noOrderEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-7882" />
                    </div>

                    <div data-id="element-7883">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-7884">
                        <input type="checkbox" checked={noOrderEvidenceChecked} onChange={e => setNoOrderEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-7885" />
                        <span className="text-sm text-gray-900" data-id="element-7886">
                          I do not have any evidence to provide for this step
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-7887">
                    <button onClick={() => setChallengeStep('evidence')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-7888">
                      Back
                    </button>
                    <button onClick={handleOrderNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-7889">
                      Next
                    </button>
                  </div>
                </div>}

              {challengeStep === 'customer' && <div className="max-w-3xl" data-id="element-7890">
                  <div className="flex items-start justify-between mb-6" data-id="element-7891">
                    <div data-id="element-7892">
                      <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-7893">
                        Customer details
                      </h1>
                      <p className="text-gray-600" data-id="element-7894">
                        Please provide as much information as possible about the
                        customer. This will allow the issuer to verify that the
                        evidence you provided corresponds to the customer
                        disputing the transaction.
                      </p>
                      <p className="text-gray-600 mt-2" data-id="element-7895">
                        The details below have been pre-filled from transaction
                        data. Please review and edit the information if needed.
                      </p>
                    </div>
                    <div className="text-right" data-id="element-7896">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-7897">
                        Dispute ID: {dispute.disputeId}
                      </div>
                      <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-7898">
                        View details
                      </button>
                      <div className="mt-2 text-sm" data-id="element-7899">
                        <div className="text-gray-500" data-id="element-7900">Dispute amount</div>
                        <div className="font-medium" data-id="element-7901">
                          {formatCurrency(dispute.amount)}
                        </div>
                      </div>
                      <div className="mt-2 text-sm" data-id="element-7902">
                        <div className="text-gray-500" data-id="element-7903">Response due</div>
                        <div className="font-medium" data-id="element-7904">
                          {formatDateTime(dispute.dueDate)}
                        </div>
                        <div className="text-red-600 font-medium" data-id="element-7905">
                          Due in 1 hour
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6" data-id="element-7906">
                    <div data-id="element-7907">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-7908">
                        Customer name
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-7909">
                        <span data-id="element-7910">Test name</span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-7911">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-7912">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-7913" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-7914">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-7915">
                        Customer email
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-7916">
                        <span data-id="element-7917">jessica@airwallex.com</span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-7918">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-7919">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-7920" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-7921">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-7922">
                        Customer purchase IP
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-7923">
                        <span data-id="element-7924">35.221.170.121</span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-7925">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-7926">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-7927" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-7928">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-7929">
                        Customer phone
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-7930">
                        <span data-id="element-7931">+60 12 345 6789</span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-7932">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-7933">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-7934" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-7935">
                      <label className="block text-sm font-medium text-gray-900 mb-2" data-id="element-7936">
                        Customer billing address
                      </label>
                      <div className="flex items-center gap-2 text-sm text-gray-700" data-id="element-7937">
                        <span data-id="element-7938">
                          Rabbit Gateway Office, 123 Silom Road, Bang Rak,
                          Bangkok 10500, Thailand
                        </span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-7939">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-7940">
                            <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.4191 1.44775 12.6666 1.44775C12.9142 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4088 2.61178C14.5035 2.84055 14.5523 3.08575 14.5523 3.33337C14.5523 3.58099 14.5035 3.82619 14.4088 4.05497C14.314 4.28374 14.1751 4.49161 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-7941" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div data-id="element-7942">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-7943">
                        Provide supporting evidence
                      </h3>
                      <p className="text-sm text-gray-600 mb-4" data-id="element-7944">
                        We recommend you to provide the following information
                        about the customer:
                      </p>
                      <p className="text-sm text-gray-600 mb-4" data-id="element-7945">
                        You can upload any document containing such information
                        (e.g. invoices, receipts) below.
                      </p>
                      <p className="text-sm text-gray-500 mb-4" data-id="element-7946">
                        {(() => {
                      const requirements = getFileFormatRequirements();
                      return <>
                              {requirements.formats}
                              <br data-id="element-7947" />
                              {requirements.language}
                              <br data-id="element-7948" />
                              {requirements.files}
                            </>;
                    })()}
                      </p>
                    </div>

                    <div data-id="element-7949">
                      <button disabled={noCustomerEvidenceChecked} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noCustomerEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-7950">
                        <UploadIcon size={16} data-id="element-7951" />
                        Upload file
                      </button>
                    </div>

                    <div data-id="element-7952">
                      <textarea placeholder="Include any additional file descriptions or explanations here. (Optional)" rows={4} disabled={noCustomerEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noCustomerEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-7953" />
                    </div>

                    <div data-id="element-7954">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-7955">
                        <input type="checkbox" checked={noCustomerEvidenceChecked} onChange={e => setNoCustomerEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-7956" />
                        <span className="text-sm text-gray-900" data-id="element-7957">
                          I do not have any evidence to provide for this step
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-7958">
                    <button onClick={() => setChallengeStep('order')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-7959">
                      Back
                    </button>
                    <button onClick={handleCustomerNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-7960">
                      Next
                    </button>
                  </div>
                </div>}

              {challengeStep === 'other' && <div className="max-w-3xl" data-id="element-7961">
                  <div className="flex items-start justify-between mb-6" data-id="element-7962">
                    <div data-id="element-7963">
                      <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-7964">
                        Other supporting documents
                      </h1>
                      <p className="text-gray-600" data-id="element-7965">
                        If you have any other document(s) that you believe is
                        valuable to assess the legitimacy of your challenge, you
                        can add it below.
                      </p>
                    </div>
                    <div className="text-right" data-id="element-7966">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-7967">
                        Dispute ID: {dispute.disputeId}
                      </div>
                      <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-7968">
                        View details
                      </button>
                      <div className="mt-2 text-sm" data-id="element-7969">
                        <div className="text-gray-500" data-id="element-7970">Dispute amount</div>
                        <div className="font-medium" data-id="element-7971">
                          {formatCurrency(dispute.amount)}
                        </div>
                      </div>
                      <div className="mt-2 text-sm" data-id="element-7972">
                        <div className="text-gray-500" data-id="element-7973">Response due</div>
                        <div className="font-medium" data-id="element-7974">
                          {formatDateTime(dispute.dueDate)}
                        </div>
                        <div className="text-red-600 font-medium" data-id="element-7975">
                          Due in 1 hour
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6" data-id="element-7976">
                    <div data-id="element-7977">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-7978">
                        Provide supporting evidence
                      </h3>
                      <p className="text-sm text-gray-500 mb-4" data-id="element-7979">
                        {(() => {
                      const requirements = getFileFormatRequirements();
                      return <>
                              {requirements.formats}
                              <br data-id="element-7980" />
                              {requirements.language}
                              <br data-id="element-7981" />
                              {requirements.files}
                            </>;
                    })()}
                      </p>
                    </div>

                    <div data-id="element-7982">
                      <button disabled={noOtherEvidenceChecked} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border ${noOtherEvidenceChecked ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'}`} data-id="element-7983">
                        <UploadIcon size={16} data-id="element-7984" />
                        Upload file
                      </button>
                    </div>

                    <div data-id="element-7985">
                      <textarea placeholder="Include any additional file descriptions or explanations here. (Optional)" rows={4} disabled={noOtherEvidenceChecked} className={`w-full border rounded-md p-3 text-sm ${noOtherEvidenceChecked ? 'bg-gray-50 text-gray-400 border-gray-200' : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}`} data-id="element-7986" />
                    </div>

                    <div data-id="element-7987">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-7988">
                        <input type="checkbox" checked={noOtherEvidenceChecked} onChange={e => setNoOtherEvidenceChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-7989" />
                        <span className="text-sm text-gray-900" data-id="element-7990">
                          I do not have any evidence to provide for this step
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-7991">
                    <button onClick={() => setChallengeStep('customer')} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-7992">
                      Back
                    </button>
                    <button onClick={handleOtherNext} className="px-6 py-2 text-sm font-medium text-white bg-gray-300 rounded-md hover:bg-gray-400" data-id="element-7993">
                      Review
                    </button>
                  </div>
                </div>}

              {challengeStep === 'review' && <div className="max-w-3xl" data-id="element-7994">
                  <div className="flex items-start justify-between mb-6" data-id="element-7995">
                    <div data-id="element-7996">
                      <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-7997">
                        Review your challenge
                      </h1>
                      <p className="text-gray-600" data-id="element-7998">
                        Please confirm that the information you have provided is
                        correct before submission.
                      </p>
                    </div>
                    <div className="text-right" data-id="element-7999">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-8000">
                        Dispute ID: {dispute.disputeId}
                      </div>
                      <button className="text-sm text-blue-600 hover:text-blue-700" data-id="element-8001">
                        View details
                      </button>
                      <div className="mt-2 text-sm" data-id="element-8002">
                        <div className="text-gray-500" data-id="element-8003">Dispute amount</div>
                        <div className="font-medium" data-id="element-8004">
                          {formatCurrency(dispute.amount)}
                        </div>
                      </div>
                      <div className="mt-2 text-sm" data-id="element-8005">
                        <div className="text-gray-500" data-id="element-8006">Response due</div>
                        <div className="font-medium" data-id="element-8007">
                          {formatDateTime(dispute.dueDate)}
                        </div>
                        <div className="text-red-600 font-medium" data-id="element-8008">
                          Due in 1 hour
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6" data-id="element-8009">
                    {/* Challenge details section */}
                    <div className="bg-gray-50 rounded-lg p-6" data-id="element-8010">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-8011">
                        Challenge details
                      </h3>
                      <div className="space-y-3" data-id="element-8012">
                        <div className="flex justify-between" data-id="element-8013">
                          <span className="text-sm text-gray-600" data-id="element-8014">
                            Reason for challenge
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-8015">
                            The customer has a purchasing history with me
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-8016">
                          <span className="text-sm text-gray-600" data-id="element-8017">
                            Type of product or service
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-8018">
                            Physical product
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Transaction details section */}
                    <div className="bg-gray-50 rounded-lg p-6" data-id="element-8019">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-8020">
                        Transaction details
                      </h3>
                      <div className="space-y-3" data-id="element-8021">
                        <div className="flex justify-between" data-id="element-8022">
                          <span className="text-sm text-gray-600" data-id="element-8023">
                            Product details
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-8024">
                            IPHONE7 * 5, 100.01 THB
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-8025">
                          <span className="text-sm text-gray-600" data-id="element-8026">
                            Shipping address
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-8027">
                            Rabbit Gateway Office, 123 Silom Road, Bang Rak,
                            Bangkok 10500, Thailand
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-8028">
                          <span className="text-sm text-gray-600" data-id="element-8029">
                            Customer name
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-8030">
                            Test name
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-8031">
                          <span className="text-sm text-gray-600" data-id="element-8032">
                            Customer email
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-8033">
                            jessica@airwallex.com
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-8034">
                          <span className="text-sm text-gray-600" data-id="element-8035">
                            Customer purchase IP
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-8036">
                            35.221.170.121
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-8037">
                          <span className="text-sm text-gray-600" data-id="element-8038">
                            Customer phone
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-8039">
                            +60 12 345 6789
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-8040">
                          <span className="text-sm text-gray-600" data-id="element-8041">
                            Customer billing address
                          </span>
                          <span className="text-sm text-gray-900" data-id="element-8042">
                            Rabbit Gateway Office, 123 Silom Road, Bang Rak,
                            Bangkok 10500, Thailand
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Warning if no evidence */}
                    {(noEvidenceChecked || noOrderEvidenceChecked || noCustomerEvidenceChecked || noOtherEvidenceChecked) && <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" data-id="element-8043">
                        <AlertTriangleIcon size={20} className="text-red-600 flex-shrink-0 mt-0.5" data-id="element-8044" />
                        <p className="text-sm text-gray-900" data-id="element-8045">
                          Please attach at least one of the recommended evidence
                          types to proceed with the response.
                        </p>
                      </div>}

                    {/* Confirmation checkbox */}
                    <div data-id="element-8046">
                      <label className="flex items-start gap-3 cursor-pointer" data-id="element-8047">
                        <input type="checkbox" checked={reviewConfirmChecked} onChange={e => setReviewConfirmChecked(e.target.checked)} className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-8048" />
                        <span className="text-sm text-gray-900" data-id="element-8049">
                          I understand that the current evidence can only be
                          submitted once for the issuer's review, and cannot be
                          reused.
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="mt-8 flex items-center justify-between pt-6 border-t border-gray-200" data-id="element-8050">
                    <button onClick={handleReviewBack} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-8051">
                      Back
                    </button>
                    <button disabled={!reviewConfirmChecked} className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-8052">
                      Submit
                    </button>
                  </div>
                </div>}
            </div>
          </div>
        </div>

        {/* No Evidence Warning Modal */}
        {showNoEvidenceWarning && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-8053">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-8054">
              <div className="fixed inset-0 transition-opacity" onClick={() => setShowNoEvidenceWarning(false)} data-id="element-8055">
                <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-8056"></div>
              </div>

              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-8057">
                &#8203;
              </span>

              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-8058">
                <div className="bg-white px-6 pt-6 pb-4" data-id="element-8059">
                  <div className="flex items-start gap-4" data-id="element-8060">
                    <div className="flex-shrink-0" data-id="element-8061">
                      <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-8062">
                        <AlertTriangleIcon size={24} className="text-red-600" data-id="element-8063" />
                      </div>
                    </div>
                    <div className="flex-1" data-id="element-8064">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-8065">
                        Are you sure you want to proceed without evidence?
                      </h3>
                      <p className="text-sm text-gray-600" data-id="element-8066">
                        Submitting a challenge without supporting evidence
                        significantly reduces your chances of winning the
                        dispute. We strongly recommend providing at least one
                        type of evidence to support your case.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 px-6 py-4 flex items-center justify-end gap-3" data-id="element-8067">
                  <button onClick={() => setShowNoEvidenceWarning(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900" data-id="element-8068">
                    Go back and add evidence
                  </button>
                  <button onClick={() => {
                setShowNoEvidenceWarning(false);
                setChallengeStep('review');
              }} className="px-6 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700" data-id="element-8069">
                    Proceed anyway
                  </button>
                </div>
              </div>
            </div>
          </div>}
      </div>;
  }
  return <div className="space-y-6" data-id="element-8070">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-8071">
        <button onClick={() => navigate('/disputes')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-8072">
          <ArrowLeftIcon size={20} data-id="element-8073" />
          Back to Disputes
        </button>
      </div>

      {/* Dispute Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-8074">
        <div className="flex items-start justify-between mb-6" data-id="element-8075">
          <div className="flex items-center gap-3" data-id="element-8076">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-8077">Dispute</h1>
            {getStatusBadge(effectiveStatus)}
          </div>
          <button onClick={() => setShowHelpModal(true)} className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700" data-id="element-8078">
            {isChallenged ? 'What happens after a dispute is challenged?' : 'How to respond to a dispute?'}
            <HelpCircleIcon size={16} data-id="element-8079" />
          </button>
        </div>

        <div className="mb-6" data-id="element-8080">
          <div className="text-sm text-gray-500" data-id="element-8081">
            Dispute ID: {dispute.disputeId}
          </div>
        </div>

        {isChallenged && <div className="mb-6 p-5 bg-blue-50 border-2 border-blue-200 rounded-lg" data-id="element-8082">
            <div className="flex items-start gap-4" data-id="element-8083">
              <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0" data-id="element-8084">
                <CheckCircleIcon size={20} className="text-white" data-id="element-8085" />
              </div>
              <div className="flex-1" data-id="element-8086">
                <div className="text-base font-semibold text-gray-900 mb-2" data-id="element-8087">
                  Challenge submitted
                </div>
                <div className="text-sm text-gray-700 mb-4" data-id="element-8088">
                  Your challenge has been submitted and is being reviewed by the
                  issuing bank. You will be notified of any updates.
                </div>
                <button onClick={handleViewChallengeDetails} className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-sm transition-colors" data-id="element-8089">
                  <FileTextIcon size={18} data-id="element-8090" />
                  View challenge details
                </button>
              </div>
            </div>
          </div>}

        {dispute.status === 'won' && <div className="mb-6 p-5 bg-green-50 border-2 border-green-200 rounded-lg" data-id="element-8091">
            <div className="flex items-start gap-4" data-id="element-8092">
              <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0" data-id="element-8093">
                <CheckCircleIcon size={20} className="text-white" data-id="element-8094" />
              </div>
              <div className="flex-1" data-id="element-8095">
                <div className="text-base font-semibold text-gray-900 mb-2" data-id="element-8096">
                  Dispute won
                </div>
                <div className="text-sm text-gray-700 mb-4" data-id="element-8097">
                  The issuing bank has reviewed your evidence and ruled in your
                  favor. The disputed amount has been credited back to your
                  account.
                </div>
                <button onClick={handleViewChallengeDetails} className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 shadow-sm transition-colors" data-id="element-8098">
                  <FileTextIcon size={18} data-id="element-8099" />
                  View your submitted evidence
                </button>
              </div>
            </div>
          </div>}

        {dispute.status === 'reversed' && <div className="mb-6 p-5 bg-green-50 border-2 border-green-200 rounded-lg" data-id="element-8100">
            <div className="flex items-start gap-4" data-id="element-8101">
              <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0" data-id="element-8102">
                <CheckCircleIcon size={20} className="text-white" data-id="element-8103" />
              </div>
              <div className="flex-1" data-id="element-8104">
                <div className="text-base font-semibold text-gray-900 mb-2" data-id="element-8105">
                  Dispute reversed
                </div>
                <div className="text-sm text-gray-700" data-id="element-8106">
                  The cardholder has withdrawn their dispute. The disputed
                  amount has been credited back to your account.
                </div>
              </div>
            </div>
          </div>}

        {dispute.status === 'accepted' && <div className="mb-6 p-5 bg-orange-50 border-2 border-orange-200 rounded-lg" data-id="element-8107">
            <div className="flex items-start gap-4" data-id="element-8108">
              <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0" data-id="element-8109">
                <CheckCircleIcon size={20} className="text-white" data-id="element-8110" />
              </div>
              <div className="flex-1" data-id="element-8111">
                <div className="text-base font-semibold text-gray-900 mb-2" data-id="element-8112">
                  Dispute accepted
                </div>
                <div className="text-sm text-gray-700" data-id="element-8113">
                  You have accepted this dispute. The disputed amount will not
                  be credited back to your account.
                </div>
              </div>
            </div>
          </div>}

        {isAccepted && <div className="mb-6 p-5 bg-orange-50 border-2 border-orange-200 rounded-lg" data-id="element-8114">
            <div className="flex items-start gap-4" data-id="element-8115">
              <div className="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0" data-id="element-8116">
                <CheckCircleIcon size={20} className="text-white" data-id="element-8117" />
              </div>
              <div className="flex-1" data-id="element-8118">
                <div className="text-base font-semibold text-gray-900 mb-2" data-id="element-8119">
                  Dispute accepted
                </div>
                <div className="text-sm text-gray-700" data-id="element-8120">
                  You have accepted this dispute. The disputed amount will not
                  be credited back to your account.
                </div>
              </div>
            </div>
          </div>}

        {dispute.status === 'lost' && <div className="mb-6 p-5 bg-red-50 border-2 border-red-200 rounded-lg" data-id="element-8121">
            <div className="flex items-start gap-4" data-id="element-8122">
              <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0" data-id="element-8123">
                <XCircleIcon size={20} className="text-white" data-id="element-8124" />
              </div>
              <div className="flex-1" data-id="element-8125">
                <div className="text-base font-semibold text-gray-900 mb-2" data-id="element-8126">
                  Dispute lost
                </div>
                <div className="text-sm text-gray-700 mb-4" data-id="element-8127">
                  The issuing bank has reviewed your evidence and ruled in favor
                  of the cardholder. The disputed amount will not be credited
                  back to your account.
                </div>
                <button onClick={handleViewChallengeDetails} className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 shadow-sm transition-colors" data-id="element-8128">
                  <FileTextIcon size={18} data-id="element-8129" />
                  View your submitted evidence
                </button>
              </div>
            </div>
          </div>}

        <div className="grid grid-cols-2 gap-8 mb-6" data-id="element-8130">
          <div data-id="element-8131">
            <div className="text-sm text-gray-500 mb-1" data-id="element-8132">Dispute amount</div>
            <div className="text-2xl font-semibold text-gray-900" data-id="element-8133">
              {formatCurrency(dispute.amount)}
            </div>
          </div>
          <div data-id="element-8134">
            <div className="text-sm text-gray-500 mb-1" data-id="element-8135">Response due</div>
            <div className="text-base font-medium text-gray-900" data-id="element-8136">
              {formatDateTime(dispute.dueDate)}
            </div>
            {hoursRemaining > 0 && hoursRemaining <= 24 && !isChallenged && <div className="text-sm text-red-600 font-medium" data-id="element-8137">
                Due in {hoursRemaining}{' '}
                {hoursRemaining === 1 ? 'hour' : 'hours'}
              </div>}
          </div>
        </div>

        {showAlert && effectiveStatus === 'requires_response' && !isChallenged && !isAccepted && <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg" data-id="element-8138">
              <div className="flex items-start justify-between" data-id="element-8139">
                <div className="flex items-start gap-3" data-id="element-8140">
                  <div className="w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-8141">
                    <InfoIcon size={14} className="text-white" data-id="element-8142" />
                  </div>
                  <div data-id="element-8143">
                    <div className="text-sm font-medium text-gray-900 mb-1" data-id="element-8144">
                      Challenge this dispute with customer's purchasing history.
                    </div>
                    <div className="text-sm text-gray-700" data-id="element-8145">
                      We have identified matching transactions that prove this
                      customer has a purchasing history with you (see{' '}
                      <button onClick={() => setActiveTab('related')} className="text-blue-600 hover:text-blue-700 underline" data-id="element-8146">
                        Related orders
                      </button>
                      ). You can challenge this dispute by providing additional
                      details about these matching transactions.
                    </div>
                  </div>
                </div>
                <button onClick={() => setShowAlert(false)} className="text-gray-400 hover:text-gray-600 flex-shrink-0" data-id="element-8147">
                  <CloseIcon size={18} data-id="element-8148" />
                </button>
              </div>
            </div>}

        {isChallenged && <div className="mb-6" data-id="element-8149">
            <button onClick={handleViewChallengeDetails} className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-8150">
              View challenge details
            </button>
          </div>}

        <div className="grid grid-cols-2 gap-x-8 gap-y-4 text-sm" data-id="element-8151">
          <div data-id="element-8152">
            <div className="text-gray-500 mb-1" data-id="element-8153">Reason</div>
            <div className="flex items-center gap-2" data-id="element-8154">
              <span className="font-medium text-gray-900" data-id="element-8155">
                {getReasonLabel(dispute.reason)}
              </span>
              <div className="relative inline-block" data-id="element-8156">
                <button onMouseEnter={() => setShowReasonTooltip(true)} onMouseLeave={() => setShowReasonTooltip(false)} className="inline-flex items-center" data-id="element-8157">
                  <InfoIcon size={14} className="text-gray-400 hover:text-gray-600" data-id="element-8158" />
                </button>
                {showReasonTooltip && <div className="absolute z-10 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg bottom-full left-0 mb-2" data-id="element-8159">
                    {getReasonDescription(dispute.reason)}
                    <div className="absolute bottom-0 left-4 transform translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900" data-id="element-8160"></div>
                  </div>}
              </div>
            </div>
          </div>
          <div data-id="element-8161">
            <div className="text-gray-500 mb-1" data-id="element-8162">Network reason code</div>
            <div className="font-medium text-gray-900" data-id="element-8163">
              {dispute.networkReasonCode}
            </div>
          </div>
          <div data-id="element-8164">
            <div className="text-gray-500 mb-1" data-id="element-8165">Dispute stage</div>
            <div className="font-medium text-gray-900 capitalize" data-id="element-8166">
              {dispute.stage.replace(/_/g, ' ')}
            </div>
          </div>
          <div data-id="element-8167">
            <div className="text-gray-500 mb-1" data-id="element-8168">Dispute time</div>
            <div className="font-medium text-gray-900" data-id="element-8169">
              {formatDateTime(dispute.createdAt)}
            </div>
          </div>
          <div data-id="element-8170">
            <div className="text-gray-500 mb-1" data-id="element-8171">Transaction type</div>
            <div className="font-medium text-gray-900" data-id="element-8172">Payment</div>
          </div>
          <div data-id="element-8173">
            <div className="text-gray-500 mb-1" data-id="element-8174">Issuer comments</div>
            <div className="font-medium text-gray-900" data-id="element-8175">Recall</div>
          </div>
        </div>

        {dispute.status === 'requires_response' && !isChallenged && !isAccepted && <div className="flex items-center gap-3 mt-6 pt-6 border-t border-gray-200" data-id="element-8176">
              <button onClick={handleChallengeClick} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-600 hover:border-blue-700 rounded-md" data-id="element-8177">
                Challenge
              </button>
              <button onClick={handleAcceptClick} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md" data-id="element-8178">
                Accept
              </button>
            </div>}
      </div>

      {/* Tabs Section */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-8179">
        <div className="border-b border-gray-200" data-id="element-8180">
          <nav className="flex gap-8 px-6" aria-label="Tabs" data-id="element-8181">
            <button onClick={() => setActiveTab('payment')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'payment' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-8182">
              Payment summary
            </button>
            <button onClick={() => setActiveTab('related')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'related' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-8183">
              Related orders
            </button>
          </nav>
        </div>

        {/* Payment Summary Tab Content */}
        {activeTab === 'payment' && <div className="p-6" data-id="element-8184">
            <div className="space-y-4 text-sm" data-id="element-8185">
              <div data-id="element-8186">
                <div className="text-gray-500 mb-1" data-id="element-8187">Order ID</div>
                <div className="flex items-center gap-2" data-id="element-8188">
                  <span className="font-medium text-gray-900" data-id="element-8189">
                    {dispute.transactionId}
                  </span>
                  <div className="relative group" data-id="element-8190">
                    <button onClick={() => handleCopyOrderId(dispute.transactionId)} className="text-gray-400 hover:text-blue-600 transition-colors" title="Copy Order ID" data-id="element-8191">
                      {copiedOrderId === dispute.transactionId ? <CheckIcon size={16} className="text-green-600" data-id="element-8192" /> : <CopyIcon size={16} data-id="element-8193" />}
                    </button>
                    {copiedOrderId === dispute.transactionId && <div className="absolute left-1/2 -translate-x-1/2 -top-8 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap" data-id="element-8194">
                        Copied!
                      </div>}
                  </div>
                </div>
              </div>
              <div data-id="element-8195">
                <div className="text-gray-500 mb-1" data-id="element-8196">Source</div>
                <div className="flex items-center gap-2" data-id="element-8197">
                  {(() => {
                const {
                  name,
                  Icon
                } = getPaymentSource();
                return <>
                        <Icon size={16} className="text-gray-600" data-id="element-8198" />
                        <span className="font-medium text-gray-900" data-id="element-8199">
                          {name}
                        </span>
                      </>;
              })()}
                </div>
              </div>
              <div data-id="element-8200">
                <div className="text-gray-500 mb-1" data-id="element-8201">Payment type</div>
                <div className="font-medium text-gray-900" data-id="element-8202">Debit card</div>
              </div>
              <div data-id="element-8203">
                <div className="text-gray-500 mb-1" data-id="element-8204">Payment method</div>
                <div className="flex items-center gap-2" data-id="element-8205">
                  <span className="font-medium text-gray-900" data-id="element-8206">
                    {dispute.cardBrand} •••• {dispute.cardLast4}
                  </span>
                </div>
              </div>
              <div data-id="element-8207">
                <div className="text-gray-500 mb-1" data-id="element-8208">Cardholder name</div>
                <div className="font-medium text-gray-900" data-id="element-8209">
                  {dispute.customerName}
                </div>
              </div>
              <div data-id="element-8210">
                <div className="text-gray-500 mb-1" data-id="element-8211">Customer purchase IP</div>
                <div className="font-medium text-gray-900" data-id="element-8212">35.221.170.121</div>
              </div>
              <div data-id="element-8213">
                <div className="text-gray-500 mb-1" data-id="element-8214">Customer phone</div>
                <div className="font-medium text-gray-900" data-id="element-8215">+60 12 345 6789</div>
              </div>
              <div data-id="element-8216">
                <div className="text-gray-500 mb-1" data-id="element-8217">Security checks</div>
                <div className="space-y-1" data-id="element-8218">
                  <div className="flex items-center gap-2 text-green-600" data-id="element-8219">
                    <CheckCircleIcon size={16} data-id="element-8220" />
                    <span data-id="element-8221">Card security code</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-400" data-id="element-8222">
                    <XCircleIcon size={16} data-id="element-8223" />
                    <span data-id="element-8224">3DS</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-400" data-id="element-8225">
                    <XCircleIcon size={16} data-id="element-8226" />
                    <span data-id="element-8227">Billing address verification</span>
                  </div>
                </div>
              </div>
              <a href="#" className="text-blue-600 hover:text-blue-700 text-sm font-medium inline-block" data-id="element-8228">
                View payment details
              </a>
            </div>
          </div>}

        {/* Related Orders Tab Content */}
        {activeTab === 'related' && <div className="p-6" data-id="element-8229">
            <div className="mb-4 text-sm text-gray-600" data-id="element-8230">
              Customers may have a purchasing history with you, which could be
              used as evidence to challenge the claim that there is a fraudulent
              charge. To help you define this history, we have identified orders
              that qualify as{' '}
              <span className="font-medium" data-id="element-8231">
                matching transactions to the issuer
              </span>
              .{' '}
              <div className="relative inline-block" data-id="element-8232">
                <button onMouseEnter={() => setShowMatchingCriteriaTooltip(true)} onMouseLeave={() => setShowMatchingCriteriaTooltip(false)} className="inline-flex items-center" data-id="element-8233">
                  <InfoIcon size={14} className="text-gray-400 hover:text-gray-600" data-id="element-8234" />
                </button>
                {showMatchingCriteriaTooltip && <div className="absolute z-10 w-96 p-4 bg-gray-900 text-white text-xs rounded-lg shadow-lg bottom-full left-0 mb-2" data-id="element-8235">
                    <div className="space-y-2" data-id="element-8236">
                      <p data-id="element-8237">
                        To qualify as a matching transaction to the issuer, a
                        transaction must meet the following criteria:
                      </p>
                      <ul className="list-disc pl-4 space-y-1" data-id="element-8238">
                        <li data-id="element-8239">
                          At least two data elements (IP address, device ID /
                          fingerprint, shipping address, email address) must
                          match across all three transactions, and one of these
                          two must be the IP address or device ID or fingerprint
                        </li>
                        <li data-id="element-8240">
                          Between 120 and 365 days older than the date of the
                          disputed transaction
                        </li>
                        <li data-id="element-8241">
                          Undisputed, and not have been reported as fraudulent
                        </li>
                      </ul>
                      <p data-id="element-8242">
                        We also show you if the payment method matches, which
                        can be used to support your challenge.
                      </p>
                    </div>
                    <div className="absolute bottom-0 left-4 transform translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900" data-id="element-8243"></div>
                  </div>}
              </div>
            </div>

            {/* Related Orders Table */}
            <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-8244">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-8245">
                <thead className="bg-gray-50" data-id="element-8246">
                  <tr data-id="element-8247">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8248">
                      Order ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8249">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8250">
                      Created Time
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8251">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8252">
                      Matching Criteria
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-8253">
                  {paginatedOrders.map(order => <tr key={order.id} className="hover:bg-gray-50" data-id="element-8254">
                      <td className="px-4 py-3" data-id="element-8255">
                        <div className="flex items-center gap-2" data-id="element-8256">
                          <span className="text-sm text-gray-900" data-id="element-8257">
                            {order.orderId}
                          </span>
                          <button className="text-blue-600 hover:text-blue-700" data-id="element-8258">
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-8259">
                              <path d="M13.3333 6H7.33333C6.59695 6 6 6.59695 6 7.33333V13.3333C6 14.0697 6.59695 14.6667 7.33333 14.6667H13.3333C14.0697 14.6667 14.6667 14.0697 14.6667 13.3333V7.33333C14.6667 6.59695 14.0697 6 13.3333 6Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-8260" />
                              <path d="M3.33333 10H2.66667C2.31304 10 1.97391 9.85952 1.72386 9.60947C1.47381 9.35942 1.33333 9.02029 1.33333 8.66667V2.66667C1.33333 2.31304 1.47381 1.97391 1.72386 1.72386C1.97391 1.47381 2.31304 1.33333 2.66667 1.33333H8.66667C9.02029 1.33333 9.35942 1.47381 9.60947 1.72386C9.85952 1.97391 10 2.31304 10 2.66667V3.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-8261" />
                            </svg>
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900" data-id="element-8262">
                        {formatCurrency(order.amount, order.currency)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900" data-id="element-8263">
                        {order.createdTime}
                      </td>
                      <td className="px-4 py-3" data-id="element-8264">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-8265">
                          {order.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right" data-id="element-8266">
                        <button onClick={() => setSelectedOrder(order)} className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-8267">
                          {order.matchingCriteria} of {order.totalCriteria}{' '}
                          criteria
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>

              {/* Pagination */}
              <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200" data-id="element-8268">
                <div className="flex items-center gap-2" data-id="element-8269">
                  <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-8270">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-8271">
                      <path d="M11 12L7 8L11 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-8272" />
                      <path d="M5 12L5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-8273" />
                    </svg>
                  </button>
                  <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-8274">
                    <ChevronLeftIcon size={16} data-id="element-8275" />
                  </button>
                  {Array.from({
                length: totalPages
              }, (_, i) => i + 1).map(page => <button key={page} onClick={() => setCurrentPage(page)} className={`px-3 py-1 text-sm rounded ${currentPage === page ? 'bg-blue-600 text-white' : 'hover:bg-gray-200 text-gray-700'}`} data-id="element-8276">
                      {page}
                    </button>)}
                  <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-8277">
                    <ChevronRightIcon size={16} data-id="element-8278" />
                  </button>
                  <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-8279">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-8280">
                      <path d="M5 4L9 8L5 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-8281" />
                      <path d="M11 4L11 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-8282" />
                    </svg>
                  </button>
                </div>
                <div className="flex items-center gap-4" data-id="element-8283">
                  <div className="text-sm text-gray-700" data-id="element-8284">
                    Rows per page:{' '}
                    <span className="font-medium" data-id="element-8285">{rowsPerPage}</span>
                  </div>
                  <div className="text-sm text-gray-700" data-id="element-8286">
                    {(currentPage - 1) * rowsPerPage + 1} -{' '}
                    {Math.min(currentPage * rowsPerPage, relatedOrders.length)}{' '}
                    of {relatedOrders.length}
                  </div>
                </div>
              </div>
            </div>
          </div>}
      </div>

      {/* Payment Breakdown Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-8287">
        <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-8288">
          Payment breakdown
        </h3>
        <div className="space-y-3 max-w-md" data-id="element-8289">
          <div className="flex items-center justify-between" data-id="element-8290">
            <div className="text-sm text-gray-600" data-id="element-8291">Payment amount</div>
            <div className="text-sm font-medium text-gray-900" data-id="element-8292">
              {formatCurrency(paymentAmount)}
            </div>
          </div>
          <div className="flex items-center justify-between" data-id="element-8293">
            <div className="flex items-center gap-1" data-id="element-8294">
              <span className="text-sm text-gray-600" data-id="element-8295">
                Final disputed amount
              </span>
              <InfoIcon size={14} className="text-gray-400" data-id="element-8296" />
            </div>
            <div className="text-sm font-medium text-gray-900" data-id="element-8297">
              - {formatCurrency(disputedAmount)}
            </div>
          </div>
          <div className="flex items-center justify-between" data-id="element-8298">
            <div className="flex items-center gap-1" data-id="element-8299">
              <span className="text-sm text-gray-600" data-id="element-8300">Dispute fee</span>
              <InfoIcon size={14} className="text-gray-400" data-id="element-8301" />
            </div>
            <div className="text-sm font-medium text-gray-900" data-id="element-8302">
              - {formatCurrency(disputeFee)}
            </div>
          </div>
          <div className="pt-3 border-t border-gray-200" data-id="element-8303">
            <div className="flex items-center justify-between" data-id="element-8304">
              <div className="text-sm font-semibold text-gray-900" data-id="element-8305">
                Net amount
              </div>
              <div className="text-sm font-semibold text-gray-900" data-id="element-8306">
                - {formatCurrency(netAmount)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-8307">
        <div className="flex items-center justify-between mb-4" data-id="element-8308">
          <h3 className="text-lg font-semibold text-gray-900" data-id="element-8309">
            Recent Activity
          </h3>
          <button onClick={() => setShowAddNote(!showAddNote)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-600 hover:border-blue-700 rounded-md transition-colors" data-id="element-8310">
            <PenIcon size={16} data-id="element-8311" />
            Add note
          </button>
        </div>

        {/* Add Note Form */}
        {showAddNote && <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-8312">
            <textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add a note for internal reference..." rows={3} className="w-full border border-gray-300 rounded-md p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-3" data-id="element-8313" />
            <div className="flex items-center justify-end gap-2" data-id="element-8314">
              <button onClick={() => {
            setShowAddNote(false);
            setNoteText('');
          }} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900" data-id="element-8315">
                Cancel
              </button>
              <button onClick={handleAddNote} disabled={!noteText.trim()} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-8316">
                Add note
              </button>
            </div>
          </div>}

        <div className="space-y-4" data-id="element-8317">
          {reversedLifecycle.map((event, index) => <div key={event.id} className="flex gap-4" data-id="element-8318">
              <div className="flex flex-col items-center" data-id="element-8319">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${event.status === 'note' ? 'bg-purple-100' : index === 0 ? 'bg-blue-100' : 'bg-gray-100'}`} data-id="element-8320">
                  {event.status === 'note' ? <FileTextIcon size={16} className="text-purple-600" data-id="element-8321" /> : <ClockIcon size={16} className={index === 0 ? 'text-blue-600' : 'text-gray-400'} data-id="element-8322" />}
                </div>
                {index < reversedLifecycle.length - 1 && <div className="w-0.5 h-12 bg-gray-200 my-1" data-id="element-8323"></div>}
              </div>
              <div className="flex-1 pb-4" data-id="element-8324">
                <div className="flex items-start justify-between gap-2" data-id="element-8325">
                  <div className="flex-1" data-id="element-8326">
                    {event.status === 'note' && <div className="flex items-center gap-2 mb-1" data-id="element-8327">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded" data-id="element-8328">
                          {event.isPinned && <PinIcon size={12} data-id="element-8329" />}
                          Internal Note
                        </span>
                      </div>}
                    {editingNoteId === event.id ? <div className="space-y-2" data-id="element-8330">
                        <textarea value={editingNoteText} onChange={e => setEditingNoteText(e.target.value)} rows={3} className="w-full border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-8331" />
                        <div className="flex items-center gap-2" data-id="element-8332">
                          <button onClick={() => handleSaveEdit(event.id)} className="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700" data-id="element-8333">
                            Save
                          </button>
                          <button onClick={handleCancelEdit} className="px-3 py-1 text-xs font-medium text-gray-700 hover:text-gray-900" data-id="element-8334">
                            Cancel
                          </button>
                        </div>
                      </div> : <>
                        <div className="text-sm font-medium text-gray-900" data-id="element-8335">
                          {event.description}
                        </div>
                        <div className="text-xs text-gray-500 mt-1" data-id="element-8336">
                          {formatDateTime(event.timestamp)}
                          {event.status === 'note' && event.author && <span data-id="element-8337"> by {event.author}</span>}
                        </div>
                      </>}
                  </div>
                  {event.status === 'note' && editingNoteId !== event.id && <div className="relative note-menu-container" data-id="element-8338">
                      <button onClick={() => setShowNoteMenu(showNoteMenu === event.id ? null : event.id)} className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100" data-id="element-8339">
                        <MoreVerticalIcon size={16} data-id="element-8340" />
                      </button>
                      {showNoteMenu === event.id && <div className="absolute right-0 top-8 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10" data-id="element-8341">
                          <button onClick={() => handlePinNote(event.id)} className="w-full px-4 py-2 text-left text-sm text-blue-600 hover:bg-gray-50 flex items-center gap-2" data-id="element-8342">
                            <PinIcon size={14} data-id="element-8343" />
                            {event.isPinned ? 'Unpin from top' : 'Pin to the top'}
                          </button>
                          <button onClick={() => handleEditNote(event.id)} className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2" data-id="element-8344">
                            <PenIcon size={14} data-id="element-8345" />
                            Edit
                          </button>
                          <button onClick={() => handleDeleteNote(event.id)} className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-50 flex items-center gap-2" data-id="element-8346">
                            <TrashIcon size={14} data-id="element-8347" />
                            Delete
                          </button>
                        </div>}
                    </div>}
                </div>
              </div>
            </div>)}
        </div>
      </div>

      {/* Challenge Modal */}
      {showChallengeModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-8348">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-8349">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowChallengeModal(false)} data-id="element-8350">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-8351"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-8352">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-8353">
              <div className="bg-white px-6 pt-6 pb-4" data-id="element-8354">
                <div className="flex items-start justify-between mb-4" data-id="element-8355">
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-8356">
                    You are challenging the dispute
                  </h2>
                  <button onClick={() => setShowChallengeModal(false)} className="text-gray-400 hover:text-gray-600" data-id="element-8357">
                    <CloseIcon size={20} data-id="element-8358" />
                  </button>
                </div>

                <p className="text-sm text-gray-600 mb-6" data-id="element-8359">
                  Here's a preview of what will happen next:
                </p>

                <div className="space-y-6" data-id="element-8360">
                  <div className="flex gap-4" data-id="element-8361">
                    <div className="flex flex-col items-center" data-id="element-8362">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-8363">
                        <PenIcon size={20} className="text-blue-600" data-id="element-8364" />
                      </div>
                      <div className="w-0.5 h-full bg-gray-200 mt-2" data-id="element-8365"></div>
                    </div>
                    <div className="flex-1 pb-6" data-id="element-8366">
                      <h3 className="text-sm font-semibold text-gray-900 mb-1" data-id="element-8367">
                        You submit the challenge
                      </h3>
                      <p className="text-sm text-gray-600" data-id="element-8368">
                        We'll guide you to provide the most compelling evidence
                        to support your challenge.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4" data-id="element-8369">
                    <div className="flex flex-col items-center" data-id="element-8370">
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0" data-id="element-8371">
                        <div className="w-3 h-3 rounded-full border-2 border-gray-400" data-id="element-8372"></div>
                      </div>
                    </div>
                    <div className="flex-1" data-id="element-8373">
                      <h3 className="text-sm font-semibold text-gray-900 mb-1" data-id="element-8374">
                        Issuer reviews the evidence & decides on next steps
                      </h3>
                      <p className="text-sm text-gray-600 mb-2" data-id="element-8375">
                        If the issuer is convinced by the submitted evidence,
                        the disputed amount will be credited back to you with
                        the next settlement batch. Please note that the
                        chargeback fee will not be credited to you.
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-8376">
                        If they are not convinced by the submitted evidence,
                        they will decline the evidence and the dispute will be
                        lost. No further action is possible.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-4 flex items-center justify-end gap-3" data-id="element-8377">
                <button onClick={() => setShowChallengeModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900" data-id="element-8378">
                  Cancel
                </button>
                <button onClick={handleChallengeConfirm} className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" data-id="element-8379">
                  Challenge
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Evidence Form Modal - keeping existing */}
      {showEvidenceForm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-8380">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-8381">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowEvidenceForm(false)} data-id="element-8382">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-8383"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-8384">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full" data-id="element-8385">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-8386">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4" data-id="element-8387">
                  Submit Evidence
                </h3>
                <div className="space-y-4" data-id="element-8388">
                  <div data-id="element-8389">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8390">
                      Customer Communication
                    </label>
                    <textarea rows={3} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Describe any communication with the customer..." data-id="element-8391" />
                  </div>
                  <div data-id="element-8392">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8393">
                      Shipping/Delivery Documentation
                    </label>
                    <textarea rows={2} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Tracking numbers, delivery confirmation, etc..." data-id="element-8394" />
                  </div>
                  <div data-id="element-8395">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-8396">
                      Upload Supporting Documents
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center" data-id="element-8397">
                      <UploadIcon size={32} className="mx-auto text-gray-400 mb-2" data-id="element-8398" />
                      <div className="text-sm text-gray-600" data-id="element-8399">
                        Drag and drop files here, or click to browse
                      </div>
                      <div className="text-xs text-gray-500 mt-1" data-id="element-8400">
                        PDF, JPG, PNG up to 10MB each
                      </div>
                      <input type="file" multiple className="hidden" data-id="element-8401" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-8402">
                <button type="button" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm" onClick={() => setShowEvidenceForm(false)} data-id="element-8403">
                  Submit Evidence
                </button>
                <button type="button" className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" onClick={() => setShowEvidenceForm(false)} data-id="element-8404">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Matching Criteria Side Panel */}
      {selectedOrder && <div className="fixed inset-0 z-50 overflow-hidden" data-id="element-8405">
          <div className="absolute inset-0 overflow-hidden" data-id="element-8406">
            <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setSelectedOrder(null)} data-id="element-8407"></div>
            <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex" data-id="element-8408">
              <div className="w-screen max-w-md" data-id="element-8409">
                <div className="h-full flex flex-col bg-white shadow-xl overflow-y-auto" data-id="element-8410">
                  {/* Header */}
                  <div className="px-6 py-4 border-b border-gray-200" data-id="element-8411">
                    <div className="flex items-center justify-between" data-id="element-8412">
                      <h2 className="text-lg font-semibold text-gray-900" data-id="element-8413">
                        Related Order
                      </h2>
                      <button onClick={() => setSelectedOrder(null)} className="text-gray-400 hover:text-gray-600" data-id="element-8414">
                        <CloseIcon size={20} data-id="element-8415" />
                      </button>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 px-6 py-4" data-id="element-8416">
                    {/* Order Status */}
                    <div className="mb-6" data-id="element-8417">
                      <div className="text-sm font-medium text-gray-900 mb-2" data-id="element-8418">
                        Order
                      </div>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 uppercase" data-id="element-8419">
                        {selectedOrder.status}
                      </span>
                    </div>

                    {/* Order ID */}
                    <div className="mb-6" data-id="element-8420">
                      <div className="text-sm text-gray-500 mb-1" data-id="element-8421">Order ID</div>
                      <div className="flex items-center gap-2" data-id="element-8422">
                        <span className="text-sm font-medium text-gray-900" data-id="element-8423">
                          {selectedOrder.orderId}
                        </span>
                        <button className="text-blue-600 hover:text-blue-700" data-id="element-8424">
                          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-8425">
                            <path d="M13.3333 6H7.33333C6.59695 6 6 6.59695 6 7.33333V13.3333C6 14.0697 6.59695 14.6667 7.33333 14.6667H13.3333C14.0697 14.6667 14.6667 14.0697 14.6667 13.3333V7.33333C14.6667 6.59695 14.0697 6 13.3333 6Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-8426" />
                            <path d="M3.33333 10H2.66667C2.31304 10 1.97391 9.85952 1.72386 9.60947C1.47381 9.35942 1.33333 9.02029 1.33333 8.66667V2.66667C1.33333 2.31304 1.47381 1.97391 1.72386 1.72386C1.97391 1.47381 2.31304 1.33333 2.66667 1.33333H8.66667C9.02029 1.33333 9.35942 1.47381 9.60947 1.72386C9.85952 1.97391 10 2.31304 10 2.66667V3.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-8427" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Matching Criteria */}
                    <div className="mb-6" data-id="element-8428">
                      <div className="flex items-center gap-2 mb-3" data-id="element-8429">
                        <h3 className="text-sm font-semibold text-gray-900" data-id="element-8430">
                          Matching criteria
                        </h3>
                        <InfoIcon size={14} className="text-gray-400" data-id="element-8431" />
                      </div>

                      <div className="space-y-6" data-id="element-8432">
                        {matchingCriteriaDetails.map((category, idx) => <div key={idx} data-id="element-8433">
                            <div className="text-sm font-medium text-gray-900 mb-1" data-id="element-8434">
                              {category.category}
                            </div>
                            {category.subtitle && <div className="text-xs text-gray-500 mb-2" data-id="element-8435">
                                {category.subtitle}
                              </div>}
                            <div className="space-y-2" data-id="element-8436">
                              {category.items.map((item, itemIdx) => <div key={itemIdx} className="flex items-center gap-2" data-id="element-8437">
                                  {item.matched ? <CheckCircleIcon size={16} className="text-green-600" data-id="element-8438" /> : <XCircleIcon size={16} className="text-red-600" data-id="element-8439" />}
                                  <span className="text-sm text-gray-700" data-id="element-8440">
                                    {item.label}
                                  </span>
                                </div>)}
                            </div>
                          </div>)}
                      </div>
                    </div>

                    {/* Payment Summary */}
                    <div className="border-t border-gray-200 pt-6" data-id="element-8441">
                      <h3 className="text-sm font-semibold text-gray-900 mb-4" data-id="element-8442">
                        Payment summary
                      </h3>
                      <div className="space-y-3 text-sm" data-id="element-8443">
                        <div className="flex justify-between" data-id="element-8444">
                          <span className="text-gray-600" data-id="element-8445">
                            Payment created time
                          </span>
                          <span className="text-gray-900" data-id="element-8446">
                            2025-07-06
                            <br data-id="element-8447" />
                            09:01 UTC+0800
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-8448">
                          <span className="text-gray-600" data-id="element-8449">Payment amount</span>
                          <span className="text-gray-900" data-id="element-8450">
                            {formatCurrency(selectedOrder.amount, selectedOrder.currency)}
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-8451">
                          <span className="text-gray-600" data-id="element-8452">Card type</span>
                          <span className="text-gray-900" data-id="element-8453">CREDIT</span>
                        </div>
                        <div className="flex justify-between" data-id="element-8454">
                          <span className="text-gray-600" data-id="element-8455">Payment method</span>
                          <div className="flex items-center gap-2" data-id="element-8456">
                            <span className="text-blue-600 font-medium" data-id="element-8457">
                              visa
                            </span>
                            <span className="text-gray-900" data-id="element-8458">
                              Visa •••• 9400
                            </span>
                          </div>
                        </div>
                        <div className="flex justify-between" data-id="element-8459">
                          <span className="text-gray-600" data-id="element-8460">Cardholder name</span>
                          <span className="text-gray-900" data-id="element-8461">Auto Testing</span>
                        </div>
                        <div data-id="element-8462">
                          <div className="text-gray-600 mb-2" data-id="element-8463">
                            Payment security check
                          </div>
                          <div className="space-y-1" data-id="element-8464">
                            <div className="flex items-center gap-2" data-id="element-8465">
                              <CheckCircleIcon size={14} className="text-green-600" data-id="element-8466" />
                              <span className="text-gray-700 text-xs" data-id="element-8467">
                                Card security code
                              </span>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-8468">
                              <CheckCircleIcon size={14} className="text-green-600" data-id="element-8469" />
                              <span className="text-gray-700 text-xs" data-id="element-8470">3DS</span>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-8471">
                              <XCircleIcon size={14} className="text-gray-400" data-id="element-8472" />
                              <span className="text-gray-700 text-xs" data-id="element-8473">
                                Billing address verification
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between" data-id="element-8474">
                    <button onClick={() => setSelectedOrder(null)} className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-8475">
                      Close
                    </button>
                    <button className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-8476">
                      View payment details
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}

      {/* Challenge Details Modal */}
      {showChallengeDetails && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-8477">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-8478">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowChallengeDetails(false)} data-id="element-8479">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-8480"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-8481">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full" data-id="element-8482">
              <div className="bg-white px-8 pt-8 pb-6" data-id="element-8483">
                <div className="flex items-start justify-between mb-6" data-id="element-8484">
                  <h2 className="text-2xl font-bold text-gray-900" data-id="element-8485">
                    Challenge details
                  </h2>
                  <button onClick={() => setShowChallengeDetails(false)} className="text-gray-400 hover:text-gray-600" data-id="element-8486">
                    <CloseIcon size={24} data-id="element-8487" />
                  </button>
                </div>

                <div className="space-y-6" data-id="element-8488">
                  <div data-id="element-8489">
                    <h3 className="text-base font-semibold text-gray-900 mb-4" data-id="element-8490">
                      Challenge details
                    </h3>
                    <div className="space-y-3" data-id="element-8491">
                      <div className="flex" data-id="element-8492">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-8493">
                          Reason for challenge
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-8494">
                          The customer has a purchasing history with me
                        </span>
                      </div>
                      <div className="flex" data-id="element-8495">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-8496">
                          Type of product or service
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-8497">
                          Physical product
                        </span>
                      </div>
                      <div className="flex" data-id="element-8498">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-8499">
                          Supporting evidence
                        </span>
                        <div className="flex-1" data-id="element-8500">
                          <div className="flex items-center gap-3" data-id="element-8501">
                            <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center flex-shrink-0" data-id="element-8502">
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-8503">
                                <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-8504" />
                                <path d="M14 2V8H20" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-8505" />
                              </svg>
                            </div>
                            <div className="flex-1" data-id="element-8506">
                              <div className="text-sm font-medium text-gray-900" data-id="element-8507">
                                OHMM-Cairo-Specs-She...9Jul25.pdf
                              </div>
                              <div className="text-sm text-gray-500" data-id="element-8508">
                                1.04 MB
                              </div>
                            </div>
                            <button className="text-blue-600 hover:text-blue-700" data-id="element-8509">
                              <EyeIcon size={18} data-id="element-8510" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div data-id="element-8511">
                    <h3 className="text-base font-semibold text-gray-900 mb-4" data-id="element-8512">
                      Transaction details
                    </h3>
                    <div className="space-y-3" data-id="element-8513">
                      <div className="flex" data-id="element-8514">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-8515">
                          Product details
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-8516">
                          IPHONE7 * 5, {formatCurrency(dispute.amount)}
                        </span>
                      </div>
                      <div className="flex" data-id="element-8517">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-8518">
                          Shipping address
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-8519">
                          Rabbit Gateway Office, 123 Silom Road, Bang Rak,
                          Bangkok 10500, Thailand
                        </span>
                      </div>
                      <div className="flex" data-id="element-8520">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-8521">
                          Customer name
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-8522">Test name</span>
                      </div>
                      <div className="flex" data-id="element-8523">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-8524">
                          Customer email
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-8525">
                          customer@example.com
                        </span>
                      </div>
                      <div className="flex" data-id="element-8526">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-8527">
                          Customer purchase IP
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-8528">
                          35.221.170.121
                        </span>
                      </div>
                      <div className="flex" data-id="element-8529">
                        <span className="text-sm text-gray-600 w-48 flex-shrink-0" data-id="element-8530">
                          Customer billing address
                        </span>
                        <span className="text-sm text-gray-900" data-id="element-8531">
                          Rabbit Gateway Office, 123 Silom Road, Bang Rak,
                          Bangkok 10500, Thailand
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-8 py-4 flex justify-end" data-id="element-8532">
                <button onClick={() => setShowChallengeDetails(false)} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-8533">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Help Modal */}
      {showHelpModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-8534">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-8535">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowHelpModal(false)} data-id="element-8536">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-8537"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-8538">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full" data-id="element-8539">
              <div className="bg-white px-8 pt-8 pb-6" data-id="element-8540">
                <div className="flex items-start justify-between mb-6" data-id="element-8541">
                  <h2 className="text-2xl font-bold text-gray-900" data-id="element-8542">
                    {isChallenged ? 'What happens after a dispute is challenged?' : 'How to respond to a dispute?'}
                  </h2>
                  <button onClick={() => setShowHelpModal(false)} className="text-gray-400 hover:text-gray-600" data-id="element-8543">
                    <CloseIcon size={24} data-id="element-8544" />
                  </button>
                </div>

                {isChallenged ? <div className="space-y-6" data-id="element-8545">
                    <div className="flex gap-4" data-id="element-8546">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-semibold" data-id="element-8547">
                        1
                      </div>
                      <div className="flex-1" data-id="element-8548">
                        <p className="text-gray-700" data-id="element-8549">
                          The disputed amount will be credited back to your
                          wallet in the next settlement batch. You can view the
                          settlement details in the "Dispute settlement" section
                          of the payment details page.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4" data-id="element-8550">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-semibold" data-id="element-8551">
                        2
                      </div>
                      <div className="flex-1" data-id="element-8552">
                        <p className="text-gray-700" data-id="element-8553">
                          If you agree that the dispute reason is valid, it is
                          best to accept the dispute.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4" data-id="element-8554">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-semibold" data-id="element-8555">
                        3
                      </div>
                      <div className="flex-1" data-id="element-8556">
                        <p className="text-gray-700" data-id="element-8557">
                          If the customer agrees to withdraw the dispute, you
                          still need to challenge the dispute and submit the
                          agreement as supporting evidence.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4" data-id="element-8558">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-semibold" data-id="element-8559">
                        4
                      </div>
                      <div className="flex-1" data-id="element-8560">
                        <p className="text-gray-700" data-id="element-8561">
                          If the customer is upholding the dispute but you
                          believe it is not valid, consider whether you have
                          compelling evidence to challenge the dispute with.
                        </p>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-gray-200" data-id="element-8562">
                      <p className="text-sm text-gray-600" data-id="element-8563">
                        Note that challenging the dispute can lead to higher
                        fees if the issuer is not convinced by your evidence.
                      </p>
                    </div>
                  </div> : <div className="space-y-6" data-id="element-8564">
                    <div className="flex gap-4" data-id="element-8565">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-semibold" data-id="element-8566">
                        1
                      </div>
                      <div className="flex-1" data-id="element-8567">
                        <p className="text-gray-700" data-id="element-8568">
                          Review the dispute details and if necessary contact
                          your customer to understand why they filed the
                          dispute.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4" data-id="element-8569">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-semibold" data-id="element-8570">
                        2
                      </div>
                      <div className="flex-1" data-id="element-8571">
                        <p className="text-gray-700" data-id="element-8572">
                          If you agree that the dispute reason is valid, it is
                          best to accept the dispute.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4" data-id="element-8573">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-semibold" data-id="element-8574">
                        3
                      </div>
                      <div className="flex-1" data-id="element-8575">
                        <p className="text-gray-700" data-id="element-8576">
                          If the customer agrees to withdraw the dispute, you
                          still need to challenge the dispute and submit the
                          agreement as supporting evidence.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4" data-id="element-8577">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-semibold" data-id="element-8578">
                        4
                      </div>
                      <div className="flex-1" data-id="element-8579">
                        <p className="text-gray-700" data-id="element-8580">
                          If the customer is upholding the dispute but you
                          believe it is not valid, consider whether you have
                          compelling evidence to challenge the dispute with.
                        </p>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-gray-200" data-id="element-8581">
                      <p className="text-sm text-gray-600" data-id="element-8582">
                        Note that challenging the dispute can lead to higher
                        fees if the issuer is not convinced by your evidence.
                      </p>
                    </div>
                  </div>}
              </div>

              <div className="bg-gray-50 px-8 py-4 flex justify-end" data-id="element-8583">
                <button onClick={() => setShowHelpModal(false)} className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-8584">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Accept Dispute Modal */}
      {showAcceptModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-8585">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-8586">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowAcceptModal(false)} data-id="element-8587">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-8588"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-8589">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-8590">
              <div className="bg-white px-6 pt-6 pb-4" data-id="element-8591">
                <div className="flex items-start gap-4" data-id="element-8592">
                  <div className="flex-shrink-0" data-id="element-8593">
                    <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center" data-id="element-8594">
                      <AlertTriangleIcon size={24} className="text-orange-600" data-id="element-8595" />
                    </div>
                  </div>
                  <div className="flex-1" data-id="element-8596">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-8597">
                      Accept this dispute?
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-8598">
                      Accepting this dispute is final. Once you accept, the
                      customer will receive a refund of{' '}
                      {formatCurrency(dispute.amount)}. The funding timelines
                      can vary by the issuer.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-4 flex items-center justify-end gap-3" data-id="element-8599">
                <button onClick={() => setShowAcceptModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900" data-id="element-8600">
                  Cancel
                </button>
                <button onClick={handleAcceptConfirm} className="px-6 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700" data-id="element-8601">
                  Accept dispute
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Add Note Modal */}
      {showAddNote && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-8602">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-8603">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowAddNote(false)} data-id="element-8604">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-8605"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-8606">
              &#8203;
            </span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full" data-id="element-8607">
              <div className="bg-white px-6 pt-6 pb-4" data-id="element-8608">
                <div className="flex items-start justify-between mb-4" data-id="element-8609">
                  <h2 className="text-lg font-semibold text-gray-900" data-id="element-8610">
                    Add Note
                  </h2>
                  <button onClick={() => setShowAddNote(false)} className="text-gray-400 hover:text-gray-600" data-id="element-8611">
                    <CloseIcon size={20} data-id="element-8612" />
                  </button>
                </div>

                <div className="space-y-4" data-id="element-8613">
                  <div data-id="element-8614">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8615">
                      Note
                    </label>
                    <textarea value={noteText} onChange={e => setNoteText(e.target.value)} rows={4} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Add a note for internal reference..." data-id="element-8616" />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4" data-id="element-8617">
                  <button onClick={() => setShowAddNote(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900" data-id="element-8618">
                    Cancel
                  </button>
                  <button onClick={handleAddNote} disabled={!noteText.trim()} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-8619">
                    Add Note
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}